/**
 * Cardio modalities, calorie estimation and heart-rate zones.
 *
 * Calories are estimated with the standard MET equation:
 *
 *     kcal/min = MET × 3.5 × bodyweight_kg / 200
 *
 * That is a population average, so treat the number as a consistent yardstick
 * for comparing your own sessions rather than a truth about your metabolism. If
 * the machine or your watch gives you a figure, log that instead — the app uses
 * your entered value whenever you provide one.
 */

export type CardioStyle = 'steady' | 'interval' | 'incline' | 'machine_free';

export interface CardioType {
  id: string;
  name: string;
  equipment: string;
  style: CardioStyle;
  /** MET at the intensity described in `defaultIntensity`. */
  met: number;
  defaultMinutes: number;
  defaultIntensity: string;
  /** Why you'd pick this one. */
  bestFor: string;
  /** Hard on the legs? Matters when your leg day is the next day. */
  legImpact: 'low' | 'medium' | 'high';
}

export const CARDIO_TYPES: CardioType[] = [
  {
    id: 'incline_walk',
    name: 'Incline treadmill walk',
    equipment: 'treadmill',
    style: 'incline',
    met: 6.5,
    defaultMinutes: 40,
    defaultIntensity: '8–12% incline, 5.0–5.5 km/h',
    bestFor:
      'The best fat-loss cardio you can do next to lifting. Burns well, barely touches your legs for recovery, and you can hold a conversation the whole time.',
    legImpact: 'low',
  },
  {
    id: 'steady_bike',
    name: 'Stationary bike (steady)',
    equipment: 'upright_bike',
    style: 'steady',
    met: 7.0,
    defaultMinutes: 40,
    defaultIntensity: 'Moderate resistance, 80–90 rpm',
    bestFor: 'Zero impact. The safest option the day before or after a heavy leg session.',
    legImpact: 'low',
  },
  {
    id: 'stairmaster',
    name: 'Stairmaster',
    equipment: 'stairmaster',
    style: 'steady',
    met: 9.0,
    defaultMinutes: 25,
    defaultIntensity: 'Steady climb, no leaning on the handles',
    bestFor: 'High burn per minute and hits the glutes and calves. Do not hang off the rails — it halves the work.',
    legImpact: 'medium',
  },
  {
    id: 'rower',
    name: 'Rowing machine',
    equipment: 'rower',
    style: 'steady',
    met: 7.5,
    defaultMinutes: 25,
    defaultIntensity: '22–26 strokes/min, damper 4–6',
    bestFor: 'Full body, so it doubles as upper-back work. Legs drive first, then back, then arms.',
    legImpact: 'medium',
  },
  {
    id: 'elliptical',
    name: 'Elliptical',
    equipment: 'elliptical',
    style: 'steady',
    met: 5.5,
    defaultMinutes: 40,
    defaultIntensity: 'Moderate resistance, using the arms too',
    bestFor: 'Easy on the joints. Lower burn than it feels like — use the arm handles properly.',
    legImpact: 'low',
  },
  {
    id: 'treadmill_run',
    name: 'Treadmill run',
    equipment: 'treadmill',
    style: 'steady',
    met: 10.0,
    defaultMinutes: 30,
    defaultIntensity: '9–11 km/h, flat',
    bestFor: 'Big burn and good for conditioning, but the most leg fatigue of the steady options.',
    legImpact: 'high',
  },
  {
    id: 'hiit_bike',
    name: 'HIIT — air bike',
    equipment: 'air_bike',
    style: 'interval',
    met: 11.0,
    defaultMinutes: 15,
    defaultIntensity: '20s all-out / 40s easy × 12–15',
    bestFor:
      'Maximum work in minimum time. Cap it at twice a week — it eats into leg recovery more than people admit.',
    legImpact: 'high',
  },
  {
    id: 'hiit_treadmill',
    name: 'HIIT — treadmill sprints',
    equipment: 'treadmill',
    style: 'interval',
    met: 11.5,
    defaultMinutes: 18,
    defaultIntensity: '30s hard / 90s walk × 8',
    bestFor: 'Great conditioning. Straddle the belt to start and stop rather than jumping on at speed.',
    legImpact: 'high',
  },
  {
    id: 'outdoor_run',
    name: 'Outdoor run',
    equipment: 'bodyweight',
    style: 'machine_free',
    met: 9.8,
    defaultMinutes: 35,
    defaultIntensity: 'Conversational pace',
    bestFor: 'Gets you outside. Riga in winter counts double for willpower.',
    legImpact: 'high',
  },
  {
    id: 'outdoor_walk',
    name: 'Outdoor walk',
    equipment: 'bodyweight',
    style: 'machine_free',
    met: 4.3,
    defaultMinutes: 50,
    defaultIntensity: 'Brisk, 5.5–6 km/h',
    bestFor:
      'Costs you nothing in recovery, so it stacks on top of everything else. The most underrated fat-loss tool there is.',
    legImpact: 'low',
  },
  {
    id: 'swimming',
    name: 'Swimming',
    equipment: 'bodyweight',
    style: 'machine_free',
    met: 8.0,
    defaultMinutes: 30,
    defaultIntensity: 'Continuous laps, moderate',
    bestFor: 'Full body with zero impact. Good active recovery on a sore week.',
    legImpact: 'low',
  },
  {
    id: 'ski_erg',
    name: 'SkiErg',
    equipment: 'ski_erg',
    style: 'steady',
    met: 8.0,
    defaultMinutes: 20,
    defaultIntensity: 'Steady double-pole',
    bestFor: 'Upper-body driven, so it spares the legs entirely.',
    legImpact: 'low',
  },
];

export const CARDIO_BY_ID: Record<string, CardioType> = Object.fromEntries(
  CARDIO_TYPES.map((c) => [c.id, c]),
);

/** kcal = MET × 3.5 × kg / 200 × minutes */
export function estimateCalories(met: number, bodyweightKg: number, minutes: number): number {
  return Math.round(((met * 3.5 * bodyweightKg) / 200) * minutes);
}

export interface HrZone {
  n: number;
  name: string;
  lowPct: number;
  highPct: number;
  purpose: string;
}

export const HR_ZONES: HrZone[] = [
  { n: 1, name: 'Recovery', lowPct: 0.5, highPct: 0.6, purpose: 'Warm-up, cool-down, active recovery' },
  { n: 2, name: 'Endurance', lowPct: 0.6, highPct: 0.7, purpose: 'Fat oxidation, aerobic base — most of your cardio lives here' },
  { n: 3, name: 'Tempo', lowPct: 0.7, highPct: 0.8, purpose: 'Aerobic capacity, moderately hard' },
  { n: 4, name: 'Threshold', lowPct: 0.8, highPct: 0.9, purpose: 'Hard intervals, uncomfortable' },
  { n: 5, name: 'VO2 max', lowPct: 0.9, highPct: 1.0, purpose: 'All-out sprints, only in short bursts' },
];

/**
 * Tanaka formula — more accurate across ages than the old 220-minus-age,
 * which systematically overestimates for people in their twenties.
 */
export function estimateMaxHr(age: number): number {
  return Math.round(208 - 0.7 * age);
}

export function zoneRange(age: number, zone: HrZone): [number, number] {
  const max = estimateMaxHr(age);
  return [Math.round(max * zone.lowPct), Math.round(max * zone.highPct)];
}
